import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { Sun, Moon, BookHalf, Funnel, FunnelFill } from "react-bootstrap-icons";
import { ResponsiveContainer, Tooltip, Treemap } from "recharts";

import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { getAnalyticsBrandLabel, getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { PopPeriodBanner } from "../../../components/analytics";
import { DocsSidebar, ExportButton, RankedBarDatum, RankedBarWidget, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import HeatmapMetricsTable from "../../../components/analytics/HeatmapMetricsTable";
import ContentBrandTreeTable from "../../../components/analytics/ContentBrandTreeTable";
import Logger from "../../../utils/Logger";

type ContentDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ChartDataPoint = {
    label: string;
    value: number;
    fill?: string;
};

type ChartCard = {
    key: string;
    docsKey: string;
    title: string;
    data: RankedBarDatum[];
    emptyMessage?: string;
    tooltipLabelFormatter?: (label: ReactNode) => ReactNode;
    tooltipValueFormatter?: (value: number, item: RankedBarDatum) => [string, string] | string;
    xAxisTickFormatter?: (value: number) => string;
    sortComparator?: (a: RankedBarDatum, b: RankedBarDatum) => number;
    xAxisScaleMode?: "linear" | "sqrt";
    presentation?: "standard" | "diverging";
    divergingDomain?: "balanced" | "positive";
    divergingMargin?: { left?: number; right?: number };
    valueLabelFormatter?: (value: number, item: RankedBarDatum) => string;
};

type ContentGenreActionsRow = Record<string, any>;
type ContentGenreTableRow = Record<string, any>;
type ContentBrandTableRow = Record<string, any>;
type ContentPageTableRow = Record<string, any>;
type ContentRailTableRow = Record<string, any>;
type ContentDeviceTableRow = Record<string, any>;
type RailMetricKey = "plays" | "views" | "users" | "avg_progress" | "plays_per_user" | "hours";

type SourceTreemapNode = {
    name: string;
    value: number;
    rawMetricValue: number;
    sourceLabel: string;
    plays: number;
    views: number;
    users: number;
    avgProgress: number | null;
    playsPerUser: number | null;
    hours: number;
};
type ContentEngagementMetricsResponseRow = Record<string, number | null | undefined>;

type ContentEngagementMetric = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    valueFormat?: "number" | "percent" | "decimal" | "durationHours";
    description?: string;
};
type ContentRisersFallersRow = Record<string, any>;

type WidgetLoadState = {
    isLoading: boolean;
    error: string | null;
};

type WidgetStateMap = Record<string, WidgetLoadState>;

const CONTENT_WIDGET_KEYS = [
    "content_engagement_metrics",
    "share_actions",
    "share_users",
    "share_content",
    "share_brands",
    "content_risers_fallers",
    "content_recommended_risers_fallers",
    "content_new_risers",
    "content_percentage_risers_fallers",
    "content_genre_table",
    "content_brand_table",
    "content_page_table",
    "content_rail_table",
    "content_device_table"
];

const createInitialWidgetStates = (isLoading: boolean): WidgetStateMap => {
    return CONTENT_WIDGET_KEYS.reduce<WidgetStateMap>((acc, widgetKey) => {
        acc[widgetKey] = { isLoading, error: null };
        return acc;
    }, {});
};

type ContentDashboardState = {
    isLoading: boolean;
    error: string | null;
    widgetStates: WidgetStateMap;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    genreActionsData: ChartDataPoint[];
    genreUsersData: ChartDataPoint[];
    shareContentData: ChartDataPoint[];
    shareBrandsData: ChartDataPoint[];
    contentEngagementMetrics: ContentEngagementMetric[];
    contentRisersFallersData: RankedBarDatum[];
    contentRecommendedRisersFallersData: RankedBarDatum[];
    contentNewRisersData: RankedBarDatum[];
    contentPercentageRisersFallersData: RankedBarDatum[];
    genreTableData: ContentGenreTableRow[];
    brandTableData: ContentBrandTableRow[];
    pageTableData: ContentPageTableRow[];
    railTableData: ContentRailTableRow[];
    deviceTableData: ContentDeviceTableRow[];
    isPageTableView: boolean;
    isRailTableView: boolean;
    isDeviceTableView: boolean;
    selectedPageMetric: RailMetricKey;
    selectedRailMetric: RailMetricKey;
    selectedDeviceMetric: RailMetricKey;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};


const chartColors = ["#007bff", "#20c997", "#fd7e14", "#6f42c1", "#e83e8c", "#17a2b8"];
const RAIL_METRIC_OPTIONS: Array<{ key: RailMetricKey; label: string }> = [
    { key: "plays", label: "Plays" },
    { key: "views", label: "Views" },
    { key: "users", label: "Users" },
    { key: "avg_progress", label: "Avg Progress" },
    { key: "plays_per_user", label: "Plays Per User" },
    { key: "hours", label: "Hours" }
];

class ContentDashboard extends React.Component<ContentDashboardProps, ContentDashboardState> {
    private activeRequestId = 0;
    private isMountedFlag = false;

    constructor(props: ContentDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            isLoading: false,
            error: null,
            widgetStates: createInitialWidgetStates(false),
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filtersCollapsed: false,
            isDarkTheme: true,
            genreActionsData: [],
            genreUsersData: [],
            shareContentData: [],
            shareBrandsData: [],
            contentEngagementMetrics: [],
            contentRisersFallersData: [],
            contentRecommendedRisersFallersData: [],
            contentNewRisersData: [],
            contentPercentageRisersFallersData: [],
            genreTableData: [],
            brandTableData: [],
            pageTableData: [],
            railTableData: [],
            deviceTableData: [],
            isPageTableView: false,
            isRailTableView: false,
            isDeviceTableView: false,
            selectedPageMetric: "plays",
            selectedRailMetric: "plays",
            selectedDeviceMetric: "plays",
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.isMountedFlag = true;
        this.fetchDashboardData();
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.activeRequestId += 1;
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            this.fetchDashboardData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { contentEngagementMetrics,
            genreActionsData,
            genreUsersData,
            shareContentData,
            shareBrandsData,
            contentRisersFallersData,
            contentRecommendedRisersFallersData,
            contentNewRisersData,
            contentPercentageRisersFallersData,
            appliedFilters
        } = this.state;
        const data: ExportableData[] = [];

        if (contentEngagementMetrics.length > 0) {
            data.push({
                title: "Content Engagement Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "PoP Change %", format: "percent" }
                ],
                rows: contentEngagementMetrics.map(metric => ({
                    label: metric.label,
                    value: metric.value,
                    changePercent: metric.changePercent
                })),
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (genreActionsData.length > 0) {
            data.push({
                title: "Genre Share (Actions)",
                columns: [
                    { key: "label", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreActionsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (genreUsersData.length > 0) {
            data.push({
                title: "Genre Share (Users)",
                columns: [
                    { key: "label", label: "Genre" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: genreUsersData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareContentData.length > 0) {
            data.push({
                title: "Content Share",
                columns: [
                    { key: "label", label: "Content" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareContentData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (shareBrandsData.length > 0) {
            data.push({
                title: replaceAnalyticsBrandTerminology("Brand Share", this.props.customerConfig),
                columns: [
                    { key: "label", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: shareBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        const riserWidgets: Array<{ title: string; rows: RankedBarDatum[]; metricLabel: string; format?: "percent" }> = [
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig),
                rows: contentRisersFallersData,
                metricLabel: "Start Difference"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Recommended Plays", this.props.customerConfig),
                rows: contentRecommendedRisersFallersData,
                metricLabel: "Recommended Start Difference"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 New Risers", this.props.customerConfig),
                rows: contentNewRisersData,
                metricLabel: "Current Starts"
            },
            {
                title: replaceAnalyticsBrandTerminology("Top 10 Percentage Risers & Fallers", this.props.customerConfig),
                rows: contentPercentageRisersFallersData,
                metricLabel: "Start Change %",
                format: "percent"
            }
        ];

        riserWidgets.forEach(widget => {
            if (widget.rows.length === 0) {
                return;
            }

            data.push({
                title: widget.title,
                columns: [
                    { key: "label", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "value", label: widget.metricLabel, format: widget.format === "percent" ? "percent" : "number" }
                ],
                rows: widget.rows,
                metadata: { filters: appliedFilters, pageKey: "content_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        });

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Content Dashboard",
            pageKey: "content_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private buildSidebarEntry = (widgetKey: string, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(current => current.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private updateWidgetState = async (
        requestId: number,
        widgetKey: string,
        loadResponse: () => Promise<unknown>,
        applyResponse: (response: unknown) => Partial<ContentDashboardState>
    ): Promise<void> => {
        try {
            const response = await loadResponse();

            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            const sidebarEntry = this.buildSidebarEntry(widgetKey, response);

            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: null }
                };

                return {
                    ...applyResponse(response),
                    sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                    widgetStates,
                    isLoading: Object.values(widgetStates).some(state => state.isLoading)
                } as ContentDashboardState;
            });
        } catch (error) {
            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            Logger.error(`Error fetching ${widgetKey}`, error);
            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: "Failed to load widget data" }
                };

                return {
                    widgetStates,
                    isLoading: Object.values(widgetStates).some(state => state.isLoading)
                } as ContentDashboardState;
            });
        }
    };

    private mapContentGenreActionsToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "action_count" | "user_count"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                label: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private mapContentGenreCatalogueToChartData = (
        rows: ContentGenreActionsRow[],
        metric: "unique_things" | "unique_brands"
    ): ChartDataPoint[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped = rows.map((row: ContentGenreActionsRow, index: number) => {
            const name = row.genre || `Segment ${index + 1}`;
            const value = Number(row[metric] ?? 0);
            return {
                label: String(name),
                value,
                fill: chartColors[index % chartColors.length]
            };
        }).filter((item: ChartDataPoint) => Number.isFinite(item.value) && item.value > 0);

        return mapped.slice(0, 20);
    };

    private mapContentEngagementMetrics = (
        row: ContentEngagementMetricsResponseRow,
        schema?: Record<string, { description?: string }>
    ): ContentEngagementMetric[] => {
        const metricMap: Array<{
            key: string;
            label: string;
            changeKey: string;
            valueFormat?: "number" | "percent" | "decimal" | "durationHours";
        }> = [
            { key: "video_start", label: "Video Starts", changeKey: "video_start_pop_pct" },
            { key: "views", label: "Views", changeKey: "views_pop_pct" },
            { key: "users", label: "Users", changeKey: "users_pop_pct" },
            { key: "avg_percent_watched", label: "Avg % Watched", changeKey: "avg_percent_watched_pop_pct", valueFormat: "percent" },
            { key: "hours_watched", label: "Hours Watched", changeKey: "hours_watched_pop_pct", valueFormat: "decimal" },
            { key: "video_start_per_user", label: "Video Starts / User", changeKey: "video_start_per_user_pop_pct", valueFormat: "decimal" },
            { key: "session_conversion", label: "Session Conversion", changeKey: "session_conversion_pop_pct", valueFormat: "percent" }
        ];

        return metricMap
            .filter(metric => Object.prototype.hasOwnProperty.call(row, metric.key))
            .map(metric => {
                let value = row[metric.key] ?? null;

                if (metric.key === "session_conversion" && value !== null) {
                    value *= 100;
                }

                return {
                    key: metric.key,
                    label: metric.label,
                    value,
                    changePercent: row[metric.changeKey] ?? null,
                    valueFormat: metric.valueFormat,
                    description: schema?.[metric.key]?.description
                };
            });
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return null;
    };

    private mapRisersFallersData = (
        rows: ContentRisersFallersRow[],
        metric: "video_start_diff" | "video_start_current" | "video_start_pop_pct",
        actualChangeMetric?: "video_start_diff"
    ): RankedBarDatum[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mapped: Array<RankedBarDatum | null> = rows
            .map((row: ContentRisersFallersRow) => {
                const value = this.toNumberOrNull(row?.[metric]);
                if (value === null) {
                    return null;
                }

                const actualChange = actualChangeMetric ? this.toNumberOrNull(row?.[actualChangeMetric]) : null;

                const label = String(row?.brand_name || row?.thing_brandname || "Unknown");
                const movement = String(row?.movement || "").toLowerCase();

                return {
                    label,
                    value,
                    fill: movement === "faller" || value < 0 ? "#ff5c7a" : "#4fd1c5",
                    actualChange: actualChange ?? undefined
                };
            });

        return mapped.filter((item): item is RankedBarDatum => item !== null);
    };

    private fetchDashboardData = async (): Promise<void> => {
        const { appliedFilters } = this.state;
        const requestId = ++this.activeRequestId;
        const actionsPromise = getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_actions",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        );

        this.setState({
            isLoading: true,
            error: null,
            widgetStates: createInitialWidgetStates(true),
            genreActionsData: [],
            genreUsersData: [],
            shareContentData: [],
            shareBrandsData: [],
            contentRisersFallersData: [],
            contentRecommendedRisersFallersData: [],
            contentNewRisersData: [],
            contentPercentageRisersFallersData: [],
            contentEngagementMetrics: [], genreTableData: [],
            brandTableData: [], pageTableData: [], railTableData: [], deviceTableData: [],
            sidebarEntries: []
        });

        void this.updateWidgetState(requestId, "content_engagement_metrics", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_engagement_metrics",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => {
            const result = (response as any)?.result;
            const row = Array.isArray(result) ? result[0] : null;
            return {
                contentEngagementMetrics: row && typeof row === "object"
                    ? this.mapContentEngagementMetrics(row as ContentEngagementMetricsResponseRow, (response as any)?.schema)
                    : []
            };
        });

        void this.updateWidgetState(requestId, "share_actions", () => actionsPromise, (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                genreActionsData: this.mapContentGenreActionsToChartData(rows, "action_count")
            };
        });

        void this.updateWidgetState(requestId, "share_users", () => actionsPromise, (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                genreUsersData: this.mapContentGenreActionsToChartData(rows, "user_count")
            };
        });

        void this.updateWidgetState(requestId, "share_content", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_catalogue"
        ), (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                shareContentData: this.mapContentGenreCatalogueToChartData(rows, "unique_things")
            };
        });

        void this.updateWidgetState(requestId, "share_brands", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_catalogue"
        ), (response: unknown) => {
            const rows: ContentGenreActionsRow[] = Array.isArray((response as any)?.result) ? (response as any).result : [];
            return {
                shareBrandsData: this.mapContentGenreCatalogueToChartData(rows, "unique_brands")
            };
        });

        void this.updateWidgetState(requestId, "content_genre_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            genreTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_brand_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_brand_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            brandTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_page_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_page_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            pageTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_rail_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_rail_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            railTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_device_table", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_device_table",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            deviceTableData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidgetState(requestId, "content_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentRisersFallersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_diff")
        }));

        void this.updateWidgetState(requestId, "content_recommended_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_recommended_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentRecommendedRisersFallersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_diff")
        }));

        void this.updateWidgetState(requestId, "content_new_risers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_new_risers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentNewRisersData: this.mapRisersFallersData(Array.isArray((response as any)?.result) ? (response as any).result : [], "video_start_current")
        }));

        void this.updateWidgetState(requestId, "content_percentage_risers_fallers", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_percentage_risers_fallers",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform
        ), (response: unknown) => ({
            contentPercentageRisersFallersData: this.mapRisersFallersData(
                Array.isArray((response as any)?.result) ? (response as any).result : [],
                "video_start_pop_pct",
                "video_start_diff"
            )
        }));

        const buildSidebar = (): void => {
            void requestId;
        };

        buildSidebar();
    };

    private renderBarCard = (chart: ChartCard, theme: WidgetTheme): ReactNode => {
        const { sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates[chart.key] || { isLoading: false, error: null };

        return (
            <RankedBarWidget
                key={chart.key}
                title={chart.title}
                description={sidebarEntries.find(e => e.widgetKey === chart.docsKey)?.description}
                theme={theme}
                data={chart.data}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                emptyMessage={chart.emptyMessage}
                labelFormatter={(label: string) => label
                    .replace(/[\r\n]+/g, " ")
                    .replace(/\s+/g, " ")
                    .trim()}
                tooltipLabelFormatter={chart.tooltipLabelFormatter}
                tooltipValueFormatter={chart.tooltipValueFormatter}
                xAxisTickFormatter={chart.xAxisTickFormatter}
                sortComparator={chart.sortComparator}
                xAxisScaleMode={chart.xAxisScaleMode}
                presentation={chart.presentation}
                divergingDomain={chart.divergingDomain}
                divergingMargin={chart.divergingMargin}
                valueLabelFormatter={chart.valueLabelFormatter}
            />
        );
    };

    private getChartsConfig = (): ChartCard[] => {
        const {
            genreActionsData,
            genreUsersData,
            contentRisersFallersData,
            contentRecommendedRisersFallersData,
            contentNewRisersData,
            contentPercentageRisersFallersData
        } = this.state;

        return [
            {
                key: "share_actions",
                docsKey: "share_actions",
                title: "Share of Actions",
                data: genreActionsData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = genreActionsData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Actions"];
                }
            },
            {
                key: "share_users",
                docsKey: "share_users",
                title: "Share of Users",
                data: genreUsersData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = genreUsersData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Users"];
                }
            },
            {
                key: "content_risers_fallers",
                docsKey: "content_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Plays", this.props.customerConfig),
                data: contentRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Start Difference"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging"
            },
            {
                key: "content_recommended_risers_fallers",
                docsKey: "content_recommended_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Risers & Fallers in Recommended Plays", this.props.customerConfig),
                data: contentRecommendedRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No recommendation-attributed risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Recommended Start Difference"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging"
            },
            {
                key: "content_new_risers",
                docsKey: "content_new_risers",
                title: replaceAnalyticsBrandTerminology("Top 10 New Risers", this.props.customerConfig),
                data: contentNewRisersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No new risers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => [`${value.toLocaleString()}`, "Current Starts"],
                xAxisScaleMode: "sqrt",
                presentation: "diverging",
                divergingDomain: "positive",
                divergingMargin: { left: 160 }
            },
            {
                key: "content_percentage_risers_fallers",
                docsKey: "content_percentage_risers_fallers",
                title: replaceAnalyticsBrandTerminology("Top 10 Percentage Risers & Fallers", this.props.customerConfig),
                data: contentPercentageRisersFallersData,
                emptyMessage: replaceAnalyticsBrandTerminology("No percentage risers/fallers data available for the selected date range.", this.props.customerConfig),
                tooltipLabelFormatter: (label: ReactNode) => `${getAnalyticsBrandLabel(this.props.customerConfig)}: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number, item: RankedBarDatum) => {
                    const actualChangeText = typeof item.actualChange === "number"
                        ? ` (${item.actualChange >= 0 ? "+" : ""}${item.actualChange.toLocaleString()} starts)`
                        : "";

                    return [`${value.toFixed(2)}%${actualChangeText}`, "Start Change %"];
                },
                xAxisTickFormatter: (value: number) => `${value.toFixed(0)}%`,
                xAxisScaleMode: "sqrt",
                presentation: "diverging",
                valueLabelFormatter: (value: number) => `${value.toFixed(0)}%`
            }
        ];
    };

    private getCatalogueChartsConfig = (): ChartCard[] => {
        const { shareContentData, shareBrandsData } = this.state;

        return [
            {
                key: "share_content",
                docsKey: "share_content",
                title: "Share of Content",
                data: shareContentData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = shareContentData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, "Share of Content"];
                }
            },
            {
                key: "share_brands",
                docsKey: "share_brands",
                title: replaceAnalyticsBrandTerminology("Share of Brands", this.props.customerConfig),
                data: shareBrandsData,
                tooltipLabelFormatter: (label: ReactNode) => `Genre: ${String(label ?? "")}`,
                tooltipValueFormatter: (value: number) => {
                    const total = shareBrandsData.reduce((sum, item) => sum + item.value, 0);
                    const percentage = total > 0 ? (value / total) * 100 : 0;
                    return [`${value.toLocaleString()} (${percentage.toFixed(1)}%)`, replaceAnalyticsBrandTerminology("Share of Brands", this.props.customerConfig)];
                }
            }
        ];
    };

    private renderBrandTable = (theme: WidgetTheme): ReactNode => {
        const { brandTableData, sidebarEntries, widgetStates } = this.state;
        const isRevryCustomer = this.props.customerConfig.name.toLowerCase().includes("revry")
            || this.props.customerConfig.slug.toLowerCase().includes("revry");
        const hasHierarchyShape = brandTableData.some(row =>
            Object.prototype.hasOwnProperty.call(row, "channel_type")
            && Object.prototype.hasOwnProperty.call(row, "channel")
            && Object.prototype.hasOwnProperty.call(row, "user_country")
        );
        const useTreeTable = isRevryCustomer || hasHierarchyShape;
        const widgetState = widgetStates.content_brand_table || { isLoading: false, error: null };
        return (
            <WidgetCard
                title={replaceAnalyticsBrandTerminology("Brand Metrics", this.props.customerConfig)}
                description={sidebarEntries.find(e => e.widgetKey === "content_brand_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && brandTableData.length === 0}
                emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available for the selected date range.", this.props.customerConfig)}
            >
                {useTreeTable ? (
                    <ContentBrandTreeTable
                        data={brandTableData}
                        emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available", this.props.customerConfig)}
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        customerConfig={this.props.customerConfig}
                    />
                ) : (
                    <HeatmapMetricsTable
                        data={brandTableData}
                        emptyMessage={replaceAnalyticsBrandTerminology("No brand table data available", this.props.customerConfig)}
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["thing_brandname", "brand_name", "brand"]}
                        customerConfig={this.props.customerConfig}
                    />
                )}
            </WidgetCard>
        );
    };

    private renderGenreTable = (theme: WidgetTheme): ReactNode => {
        const { genreTableData, sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates.content_genre_table || { isLoading: false, error: null };
        return (
            <WidgetCard
                title="Genre Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_genre_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && genreTableData.length === 0}
                emptyMessage="No genre table data available for the selected date range."
            >
                <HeatmapMetricsTable
                    data={genreTableData}
                    emptyMessage="No genre table data available"
                    hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                    initialSortKey="plays"
                    preferredLeadingKeys={["genre", "genre_name", "name"]}
                    customerConfig={this.props.customerConfig}
                />
            </WidgetCard>
        );
    };

    private getPageTreemapData = (rows: ContentPageTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentPageTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourcePage = String(row.source_page_rollup ?? row.source_page ?? `Page ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourcePage,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourcePage,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        const sortedRows = [...mappedRows].sort((a, b) => {
            if (b.rawMetricValue !== a.rawMetricValue) {
                return b.rawMetricValue - a.rawMetricValue;
            }

            return a.name.localeCompare(b.name);
        });

        const positiveValues = sortedRows
            .map(row => row.rawMetricValue)
            .filter(value => Number.isFinite(value) && value > 0);
        const minPositive = positiveValues.length > 0 ? Math.min(...positiveValues) : 1;
        const zeroDisplayValue = Math.max(minPositive * 0.18, 0.01);

        return sortedRows.map(row => ({
            ...row,
            value: row.rawMetricValue > 0 ? row.rawMetricValue : zeroDisplayValue
        }));
    };

    private renderPageWidget = (theme: WidgetTheme): ReactNode => {
        const { pageTableData, sidebarEntries, widgetStates, isPageTableView, selectedPageMetric } = this.state;
        const widgetState = widgetStates.content_page_table || { isLoading: false, error: null };
        const treemapData = this.getPageTreemapData(pageTableData, selectedPageMetric);

        return (
            <WidgetCard
                title="Source Page Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_page_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && pageTableData.length === 0}
                emptyMessage="No source page table data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedPageMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedPageMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <Button
                        size="sm"
                        style={{
                            backgroundColor: "transparent",
                            borderColor: theme.border,
                            color: theme.text
                        }}
                        onClick={() => this.setState(prevState => ({ isPageTableView: !prevState.isPageTableView }))}
                    >
                        {isPageTableView ? "Treemap" : "Table"}
                    </Button>
                </div>
                {isPageTableView ? (
                    <HeatmapMetricsTable
                        data={pageTableData}
                        emptyMessage="No source page table data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_page_rollup", "source_page", "page"]}
                        customerConfig={this.props.customerConfig}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedPageMetric)
                )}
            </WidgetCard>
        );
    };

    private getRailTreemapData = (rows: ContentRailTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentRailTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const sourceRail = String(row.source_rail ?? `Rail ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: sourceRail.replace(/_/g, " "),
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: sourceRail,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        const sortedRows = [...mappedRows].sort((a, b) => {
            if (b.rawMetricValue !== a.rawMetricValue) {
                return b.rawMetricValue - a.rawMetricValue;
            }

            return a.name.localeCompare(b.name);
        });

        const positiveValues = sortedRows
            .map(row => row.rawMetricValue)
            .filter(value => Number.isFinite(value) && value > 0);
        const minPositive = positiveValues.length > 0 ? Math.min(...positiveValues) : 1;
        const zeroDisplayValue = Math.max(minPositive * 0.18, 0.01);

        return sortedRows.map(row => ({
            ...row,
            value: row.rawMetricValue > 0 ? row.rawMetricValue : zeroDisplayValue
        }));
    };

    private renderRailTreemap = (data: SourceTreemapNode[], theme: WidgetTheme, metricKey: RailMetricKey): ReactNode => {
        const hasData = data.length > 0;

        if (!hasData) {
            return <Alert variant="info" style={{ marginBottom: 0 }}>No source rail data available</Alert>;
        }

        const maxValue = data.reduce((currentMax, item) => Math.max(currentMax, Number(item.value ?? 0)), 0);
        const metricLabel = RAIL_METRIC_OPTIONS.find(option => option.key === metricKey)?.label || "Metric";
        const formatMetricValue = (value: number): string => {
            if (metricKey === "avg_progress") {
                return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
            }

            return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        };
        const nodeByName = new Map<string, SourceTreemapNode>(data.map(item => [item.name, item]));
        const getNodeFromTreemapPayload = (payload: any, name?: string): SourceTreemapNode | null => {
            const candidates = [
                payload,
                payload?.payload,
                payload?.payload?.payload,
                payload?.data,
                payload?.node,
                payload?.node?.payload
            ];

            for (const candidate of candidates) {
                if (!candidate) {
                    continue;
                }

                const candidatePlays = Number(candidate.plays);
                const candidateName = typeof candidate.name === "string" ? candidate.name : undefined;

                if (Number.isFinite(candidatePlays) || candidateName) {
                    return candidate as SourceTreemapNode;
                }
            }

            if (name && nodeByName.has(name)) {
                return nodeByName.get(name) ?? null;
            }

            return null;
        };

        const truncateLabel = (value: string, width: number): string => {
            const chars = Math.max(8, Math.floor(width / 8));
            if (value.length <= chars) {
                return value;
            }

            return `${value.slice(0, Math.max(0, chars - 1))}…`;
        };

        const treemapHeight = Math.max(420, Math.ceil(data.length / 4) * 130);

        return (
            <ResponsiveContainer width="100%" height={treemapHeight}>
                <Treemap
                    data={data}
                    dataKey="value"
                    stroke={theme.border}
                    aspectRatio={1}
                    content={(props: any): React.ReactElement => {
                        const {
                            x,
                            y,
                            width,
                            height,
                            name,
                            payload
                        } = props;

                        if (width <= 0 || height <= 0) {
                            return <g />;
                        }

                        const node = getNodeFromTreemapPayload(payload, typeof name === "string" ? name : undefined);
                        const metricRaw = Number(node?.rawMetricValue ?? 0);
                        const metricValue = Number.isFinite(metricRaw) ? metricRaw : 0;
                        const valueForColor = Number(node?.value ?? payload?.value ?? 0);
                        const normalized = maxValue > 0 ? Math.max(0, Math.min(1, valueForColor / maxValue)) : 0;
                        const fill = `hsl(340, 88%, ${60 - (normalized * 28)}%)`;
                        const canRenderLabel = width > 20 && height > 14;
                        const canRenderMetric = width > 90 && height > 54;
                        const centerX = x + (width / 2);
                        const centerY = y + (height / 2);
                        const baseLabel = String(node?.name ?? name ?? "");
                        const label = truncateLabel(baseLabel, Math.max(8, width - 8));
                        const mainFontSize = Math.max(9, Math.min(20, Math.floor(Math.min(width / 6.5, height / 2.4))));
                        const valueFontSize = Math.max(12, Math.min(16, Math.floor(mainFontSize * 0.72)));
                        const clipId = `rail-cell-${Math.round(x)}-${Math.round(y)}-${Math.round(width)}-${Math.round(height)}`;
                        const textY = canRenderMetric ? centerY - (mainFontSize * 0.28) : centerY;

                        return (
                            <g>
                                <rect x={x} y={y} width={width} height={height} style={{ fill, stroke: theme.border, strokeWidth: 1 }} />
                                <clipPath id={clipId}>
                                    <rect x={x + 2} y={y + 2} width={Math.max(0, width - 4)} height={Math.max(0, height - 4)} />
                                </clipPath>
                                {canRenderLabel && (
                                    <g clipPath={`url(#${clipId})`}>
                                        <text
                                            x={centerX}
                                            y={textY}
                                            fill="#ffffff"
                                            fontSize={mainFontSize}
                                            fontWeight={800}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.38)", strokeWidth: 2 }}
                                        >
                                            {label}
                                        </text>
                                        {canRenderMetric && (
                                            <text
                                                x={centerX}
                                                y={centerY + (valueFontSize * 0.95)}
                                                fill="rgba(255,255,255,0.96)"
                                                fontSize={valueFontSize}
                                                fontWeight={700}
                                                textAnchor="middle"
                                                dominantBaseline="central"
                                                style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.32)", strokeWidth: 1.5 }}
                                            >
                                                {`${metricLabel}: ${formatMetricValue(metricValue)}`}
                                            </text>
                                        )}
                                    </g>
                                )}
                            </g>
                        );
                    }}
                >
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.chartBg,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                        itemStyle={{ color: theme.text }}
                        labelStyle={{ color: theme.text }}
                        formatter={(_value: number | string | Array<number | string> | undefined, _name?: string, details?: any) => {
                            const node = details?.payload as SourceTreemapNode | undefined;
                            if (!node) {
                                return ["", ""];
                            }

                            const avgProgress = node.avgProgress === null ? "-" : `${node.avgProgress.toFixed(2)}%`;
                            const playsPerUser = node.playsPerUser === null ? "-" : node.playsPerUser.toLocaleString(undefined, { maximumFractionDigits: 2 });
                            const selectedMetric = formatMetricValue(Number(node.rawMetricValue ?? 0));

                            return [
                                `${metricLabel}: ${selectedMetric} | Plays: ${node.plays.toLocaleString()} | Views: ${node.views.toLocaleString()} | Users: ${node.users.toLocaleString()} | Avg Progress: ${avgProgress} | Plays/User: ${playsPerUser} | Hours: ${node.hours.toLocaleString()}`,
                                node.name
                            ];
                        }}
                    />
                </Treemap>
            </ResponsiveContainer>
        );
    };

    private renderRailWidget = (theme: WidgetTheme): ReactNode => {
        const { railTableData, sidebarEntries, widgetStates, isRailTableView, selectedRailMetric } = this.state;
        const widgetState = widgetStates.content_rail_table || { isLoading: false, error: null };
        const treemapData = this.getRailTreemapData(railTableData, selectedRailMetric);

        return (
            <WidgetCard
                title="Source Rail Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_rail_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && railTableData.length === 0}
                emptyMessage="No source rail data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedRailMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedRailMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <Button
                        size="sm"
                        style={{
                            backgroundColor: "transparent",
                            borderColor: theme.border,
                            color: theme.text
                        }}
                        onClick={() => this.setState(prevState => ({ isRailTableView: !prevState.isRailTableView }))}
                    >
                        {isRailTableView ? "Treemap" : "Table"}
                    </Button>
                </div>
                {isRailTableView ? (
                    <HeatmapMetricsTable
                        data={railTableData}
                        emptyMessage="No source rail data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["source_rail", "source_rail_rollup", "rail"]}
                        customerConfig={this.props.customerConfig}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedRailMetric)
                )}
            </WidgetCard>
        );
    };

    private getDeviceTreemapData = (rows: ContentDeviceTableRow[], metricKey: RailMetricKey): SourceTreemapNode[] => {
        if (!Array.isArray(rows) || rows.length === 0) {
            return [];
        }

        const mappedRows = rows.map((row: ContentDeviceTableRow, index: number) => {
            const plays = Number(row.plays ?? 0);
            const views = Number(row.views ?? 0);
            const users = Number(row.users ?? 0);
            const hours = Number(row.hours ?? 0);
            const avgProgressRaw = row.avg_progress;
            const playsPerUserRaw = row.plays_per_user;
            const avgProgress = avgProgressRaw === null || avgProgressRaw === undefined ? null : Number(avgProgressRaw);
            const playsPerUser = playsPerUserRaw === null || playsPerUserRaw === undefined ? null : Number(playsPerUserRaw);
            const devicePlatform = String(row.device_platform ?? `Device ${index + 1}`);
            const metricValues: Record<RailMetricKey, number | null> = {
                plays: Number.isFinite(plays) ? plays : null,
                views: Number.isFinite(views) ? views : null,
                users: Number.isFinite(users) ? users : null,
                "avg_progress": Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                "plays_per_user": Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours: Number.isFinite(hours) ? hours : null
            };

            const metricValue = metricValues[metricKey];
            const rawMetricValue = metricValue === null ? 0 : Math.max(0, metricValue);

            return {
                name: devicePlatform,
                value: rawMetricValue,
                rawMetricValue,
                sourceLabel: devicePlatform,
                plays,
                views,
                users,
                avgProgress: Number.isFinite(avgProgress as number) ? (avgProgress as number) : null,
                playsPerUser: Number.isFinite(playsPerUser as number) ? (playsPerUser as number) : null,
                hours
            };
        });

        const sortedRows = [...mappedRows].sort((a, b) => {
            if (b.rawMetricValue !== a.rawMetricValue) {
                return b.rawMetricValue - a.rawMetricValue;
            }

            return a.name.localeCompare(b.name);
        });

        const positiveValues = sortedRows
            .map(row => row.rawMetricValue)
            .filter(value => Number.isFinite(value) && value > 0);
        const minPositive = positiveValues.length > 0 ? Math.min(...positiveValues) : 1;
        const zeroDisplayValue = Math.max(minPositive * 0.18, 0.01);

        return sortedRows.map(row => ({
            ...row,
            value: row.rawMetricValue > 0 ? row.rawMetricValue : zeroDisplayValue
        }));
    };

    private renderDeviceWidget = (theme: WidgetTheme): ReactNode => {
        const { deviceTableData, sidebarEntries, widgetStates, isDeviceTableView, selectedDeviceMetric } = this.state;
        const widgetState = widgetStates.content_device_table || { isLoading: false, error: null };
        const treemapData = this.getDeviceTreemapData(deviceTableData, selectedDeviceMetric);

        return (
            <WidgetCard
                title="Device Platform Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_device_table")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && deviceTableData.length === 0}
                emptyMessage="No device platform data available for the selected date range."
            >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", gap: "12px", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: theme.text, fontSize: "13px", fontWeight: 600 }}>Metric</span>
                        <Form.Control
                            as="select"
                            size="sm"
                            value={selectedDeviceMetric}
                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => this.setState({ selectedDeviceMetric: event.target.value as RailMetricKey })}
                            style={{
                                minWidth: "190px",
                                backgroundColor: theme.chartBg,
                                color: theme.text,
                                borderColor: theme.border
                            }}
                        >
                            {RAIL_METRIC_OPTIONS.map(option => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Control>
                    </div>
                    <Button
                        size="sm"
                        style={{
                            backgroundColor: "transparent",
                            borderColor: theme.border,
                            color: theme.text
                        }}
                        onClick={() => this.setState(prevState => ({ isDeviceTableView: !prevState.isDeviceTableView }))}
                    >
                        {isDeviceTableView ? "Treemap" : "Table"}
                    </Button>
                </div>
                {isDeviceTableView ? (
                    <HeatmapMetricsTable
                        data={deviceTableData}
                        emptyMessage="No device platform data available"
                        hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                        initialSortKey="plays"
                        preferredLeadingKeys={["device_platform", "platform", "device"]}
                        customerConfig={this.props.customerConfig}
                    />
                ) : (
                    this.renderRailTreemap(treemapData, theme, selectedDeviceMetric)
                )}
            </WidgetCard>
        );
    };

    private renderContentEngagementMetrics = (theme: WidgetTheme): ReactNode => {
        const { contentEngagementMetrics, sidebarEntries, widgetStates } = this.state;
        const widgetState = widgetStates.content_engagement_metrics || { isLoading: false, error: null };

        return (
            <WidgetCard
                title="Content Engagement Metrics"
                description={sidebarEntries.find(e => e.widgetKey === "content_engagement_metrics")?.description}
                theme={theme}
                isLoading={widgetState.isLoading}
                error={widgetState.error}
                isEmpty={!widgetState.isLoading && !widgetState.error && contentEngagementMetrics.length === 0}
                emptyMessage="No data for selected filters. Try widening date range or removing one or more filters."
            >
                <MetricCards
                    metrics={contentEngagementMetrics as MetricCardItem[]}
                    theme={theme}
                    hoursWatchedFormat={this.props.customerConfig.analyticsConfig?.hoursWatchedFormat}
                    emptyMessage="No data for selected filters. Try widening date range or removing one or more filters."
                    minCardWidth="200px"
                    maxCardWidth="200px"
                    gridGap="16px"
                />
            </WidgetCard>
        );
    };

    public render(): ReactNode {
        const { filters, filterOptions, appliedFilters, filtersCollapsed, isDarkTheme, error, sidebarOpen, sidebarEntries, widgetStates, contentEngagementMetrics, genreActionsData, genreUsersData } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const charts = this.getChartsConfig();
        const catalogueCharts = this.getCatalogueChartsConfig();
        const shareCharts = charts.filter(chart => ["share_actions", "share_users", "share_content", "share_brands"].includes(chart.key));
        const riserCharts = charts.filter(chart => ["content_risers_fallers", "content_recommended_risers_fallers", "content_new_risers", "content_percentage_risers_fallers"].includes(chart.key));
        const allWidgetsSettled = Object.values(widgetStates).every(state => !state.isLoading);

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container className="mw-100" key="content-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={allWidgetsSettled && !error && (contentEngagementMetrics.length > 0 || genreActionsData.length > 0 || genreUsersData.length > 0)}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleTheme}
                        style={{
                            backgroundColor: "transparent",
                            border: "none",
                            fontSize: "24px",
                            padding: "4px 8px",
                            color: isDarkTheme ? "#ffffff" : "#000000"
                        }}
                        title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                    >
                        {isDarkTheme ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={filtersCollapsed ? "Show filters" : "Hide filters"}
                    >
                        {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                    </Button>
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>

                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2 style={{ color: theme.text }}>Content Dashboard</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                    isCollapsed={filtersCollapsed}
                />
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger" style={{ marginTop: "20px" }}>{error}</Alert>
                        </Col>
                    </Row>
                )}
                <>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderContentEngagementMetrics(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderBrandTable(theme)}
                        </Col>
                    </Row>
                    <Row>
                        {shareCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "20px" }}>
                            {this.renderGenreTable(theme)}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} style={{ marginTop: "28px", textAlign: "center" }}>
                            <h4 style={{ color: theme.text, marginBottom: "8px" }}>Overall Catalogue Metrics</h4>
                            <p style={{ color: theme.text, opacity: 0.8, marginBottom: 0 }}>Filters do not apply to this section</p>
                        </Col>
                    </Row>
                    <Row>
                        {catalogueCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {riserCharts.map(chart => (
                            <Col key={chart.key} md={12} lg={6} style={{ marginTop: "20px" }}>
                                {this.renderBarCard(chart, theme)}
                            </Col>
                        ))}
                    </Row>
                </>
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            <ScheduleExportModal
                show={this.state.showScheduleModal}
                onHide={() => this.setState({ showScheduleModal: false })}
                filters={this.state.appliedFilters}
                pageKey="content_dashboard"
                pageTitle="Content Dashboard"
                customerSlug={this.props.customerConfig.slug}
                customerConfig={this.props.customerConfig}
                editingSchedule={this.state.editingSchedule}
                onSave={this.handleScheduleSave}
            />
            </div>
        );
    }
}

export default ContentDashboard;
